/**
 * Translation Admin sections — groups catalog keys the way a translator
 * experiences the app. The longest matching prefix wins; unmatched keys land in "Other".
 */

export interface TranslationSection {
  id: string;
  label: string;
  description: string;
  /** Key prefixes (matched with startsWith). */
  prefixes: string[];
}

export const TRANSLATION_SECTIONS: TranslationSection[] = [
  {
    id: 'common',
    label: 'Common UI & navigation',
    description:
      'Shared buttons, Yes/No/OK dialog buttons, sign out, relative dates, header menu, footer, step names, language picker, session errors',
    prefixes: ['ui.', 'nav.', 'i18n.', 'auth.', 'stepName.', 'dialog.', 'time.'],
  },
  {
    id: 'sign_in',
    label: 'Sign in & create account',
    description:
      'Sign-in and create-account form, email verification, password reset, account emails, server messages, invitations, pick-your-program, display name',
    prefixes: [
      'auth.form.',
      'auth.reset.',
      'auth.verify.',
      'auth.server.',
      'auth.invite.',
      'auth.display_name.',
      'email.auth.',
      'onboarding.',
    ],
  },
  {
    id: 'home',
    label: 'Home page',
    description: 'The Recovery Commons home page',
    prefixes: ['home.'],
  },
  {
    id: 'saa_home',
    label: 'SAA home page',
    description: 'Top of the SAA page: title, introduction, tab names, the note that resources are in English, and resource panel buttons',
    prefixes: ['saa_home.', 'panels.'],
  },
  {
    id: 'working_steps',
    label: 'Work my steps (hub)',
    description: 'The 12-step overview page, step cards, and journey messages',
    prefixes: ['working_steps.'],
  },
  {
    id: 'getting_started',
    label: 'Getting Started',
    description: 'Getting Started page, actions, sponsor, and 3 Circles',
    prefixes: ['getting_started.'],
  },
  {
    id: 'step_shared',
    label: 'Step pages — shared',
    description: 'Chrome used on every step: actions, help, sharing with a sponsor, sign-in prompt',
    prefixes: [
      'step.action.',
      'step.page.',
      'step.question.',
      'step.workspace.',
      'step.help.',
      'step.login_prompt.',
      'step.share.',
    ],
  },
  {
    id: 'step_1',
    label: 'Step 1 & First Step writing',
    description: 'Step 1 page, writing assistant, presentation planning',
    prefixes: ['step.1.'],
  },
  ...Array.from({ length: 11 }, (_, i) => {
    const n = i + 2;
    return {
      id: `step_${n}`,
      label: `Step ${n}`,
      description: `Step ${n} page, prompts, and workspaces`,
      prefixes: [`step.${n}.`],
    };
  }),
  {
    id: 'account',
    label: 'Profile, sponsors & invitations',
    description: 'Profile window, sponsor and sponsee lists, sending and managing invitations',
    prefixes: ['account.', 'invite.'],
  },
  {
    id: 'share_history',
    label: 'Share progress & step history',
    description: 'Share-progress window and the step dates history window',
    prefixes: ['share.', 'history.'],
  },
  {
    id: 'backup',
    label: 'Backup, saved copies & start over',
    description: 'Backup and restore, saved copies of step work, and starting over',
    prefixes: ['backup.', 'archive.', 'start_over.'],
  },
  {
    id: 'about_feedback',
    label: 'About, feedback & videos',
    description: 'About window, feedback form, video player, and the document reader window',
    prefixes: ['about.', 'feedback.', 'video.', 'content_reader.'],
  },
  {
    id: 'workshop_public',
    label: 'Workshop — About, FAQ & schedule',
    description: 'Public workshop page, FAQ, schedule, and interest sign-up',
    prefixes: [
      'workshop.2026.about.',
      'workshop.2026.faq.',
      'workshop.2026.schedule.',
      'workshop.2026.interest_modal.',
      'workshop.2026.interest_survey.',
      'workshop.2026.gate.',
      'workshop.2026.share.',
      'workshop.2026.nav_label',
      'workshop.2026.default_name',
      'workshop.2026.hero_subtitle',
      'workshop.2026.starting',
      'workshop.2026.tab_',
      'workshop.2026.loading',
    ],
  },
  {
    id: 'workshop_members',
    label: 'Workshop — member tools',
    description: 'Roster, phone list, preferences, First Step sign-up, calendar, and other member tabs',
    prefixes: ['workshop.'],
  },
];

/**
 * Keys outside the current translation effort: hidden from Translation Admin, and
 * left out of non-English catalogs so those screens show English.
 */
export const OUT_OF_SCOPE_PREFIXES = ['meetings.', 'self_assess.'];

export function isInScope(key: string): boolean {
  return !OUT_OF_SCOPE_PREFIXES.some((prefix) => key.startsWith(prefix));
}

export const OTHER_SECTION: TranslationSection = {
  id: 'other',
  label: 'Other',
  description: 'Keys not yet assigned to a section',
  prefixes: [],
};

export function sectionForKey(key: string): TranslationSection {
  let best: TranslationSection = OTHER_SECTION;
  let bestLength = 0;
  for (const section of TRANSLATION_SECTIONS) {
    for (const prefix of section.prefixes) {
      if (prefix.length > bestLength && key.startsWith(prefix)) {
        best = section;
        bestLength = prefix.length;
      }
    }
  }
  return best;
}

export function getSection(id: string): TranslationSection {
  return TRANSLATION_SECTIONS.find((s) => s.id === id) ?? OTHER_SECTION;
}
